import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { Box, Button, Paper, Typography } from '@mui/material'
import { Component, ErrorInfo, ReactNode } from 'react'
import { useTranslate } from 'react-admin'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Fallback message to display when an error occurs */
  fallbackMessage?: string
  /** Component name for error logging */
  componentName?: string
  /** Callback when an error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /** Whether to show a compact error display */
  compact?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * The fallback UI is a function component so it can translate (a class cannot use hooks).
 * `componentName` is expected ALREADY translated by the caller; it is only logged and shown.
 */
const ErrorFallback = ({
  error,
  fallbackMessage,
  componentName,
  compact,
  onRetry,
}: {
  error: Error | null
  fallbackMessage?: string
  componentName?: string
  compact?: boolean
  onRetry: () => void
}) => {
  const translate = useTranslate()
  const message =
    fallbackMessage ||
    translate('pos.error_boundary.unable_to_load', {
      name: componentName || translate('pos.error_boundary.content'),
    })

  if (compact) {
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'error.main',
          bgcolor: 'error.light',
          borderRadius: 1,
          opacity: 0.9,
        }}
      >
        <ErrorIcon sx={{ mr: 1, fontSize: 20 }} />
        <Typography variant="body2">{message}</Typography>
        <Button
          size="small"
          onClick={onRetry}
          sx={{ ml: 2, minWidth: 'auto' }}
          aria-label={translate('pos.error_boundary.try_again')}
        >
          <RefreshIcon fontSize="small" />
        </Button>
      </Box>
    )
  }

  return (
    <Paper
      sx={{
        p: 3,
        textAlign: 'center',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'error.light',
      }}
    >
      <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
      <Typography variant="h6" color="error" gutterBottom>
        {message}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('pos.error_boundary.render_error')}
        {process.env.NODE_ENV === 'development' && error && (
          <>
            <br />
            <code style={{ fontSize: '0.75rem' }}>{error.message}</code>
          </>
        )}
      </Typography>
      <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={onRetry}>
        {translate('pos.error_boundary.try_again')}
      </Button>
    </Paper>
  )
}

/**
 * Error Boundary component that catches JavaScript errors in child components
 * Prevents the entire app from crashing when a single component fails
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { componentName, onError } = this.props

    // Log error for debugging
    console.error(`Error in ${componentName || 'component'}:`, error)
    console.error('Error info:', errorInfo.componentStack)

    // Call onError callback if provided
    if (onError) {
      onError(error, errorInfo)
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    const { hasError, error } = this.state
    const { children, fallbackMessage, componentName, compact } = this.props

    if (hasError) {
      return (
        <ErrorFallback
          error={error}
          fallbackMessage={fallbackMessage}
          componentName={componentName}
          compact={compact}
          onRetry={this.handleRetry}
        />
      )
    }

    return children
  }
}

export default ErrorBoundary
